// v77.99.23 — Calendar Slice 3 refactor.
//
// DayDetailDialog — the "click on a day cell" detail modal.
// Extracted from CalendarPage.jsx (originally ~275 lines inline).
//
// This component is intentionally a presentational/state-coupled
// extraction, not a re-architecture. Every callback and lookup it
// needs is passed in as a prop so the parent's state model is
// untouched. New code should NOT add fresh fetches or business
// logic here; thread it through props instead.
//
// Why so many props:
//   The day-detail panel touches custody schedules, per-child
//   overrides, handovers, events, mediator-vs-parent permissions,
//   child-scope filters, and linked journal entries. Each helper
//   here is the same one used by other surfaces of CalendarPage,
//   so re-deriving them inside this file would risk drift.
//
// Anatomy (top → bottom):
//   1. Date title + scope breadcrumb (single-child filter banner)
//   2. Per-child custody rows (the modern layout — one card per
//      child the user is allowed to see, with parent-of-day,
//      handover info, "Swap to X" buttons, override removal).
//   3. Legacy household-wide custody block (only renders when
//      there's no per-child data — e.g. mediator viewers).
//   4. Event list for the day (type badge, recurrence pill,
//      shared/private badge, change-request badge, linked
//      journal pill, action buttons: agree/not-agree, edit,
//      request change, make journal note, add to calendar).

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ArrowLeftRight,
  Repeat,
  Eye,
  Lock,
  BookOpen,
  Pencil,
  CalendarPlus,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDT, toLocalInput } from "@/lib/format";

const TYPE_LABEL = {
  custody: "Custody",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  school: "School",
  extracurricular: "Extracurricular",
  sports: "Sports",
  holiday: "Holiday",
  call: "Phone call",
  video_call: "Video call",
  other: "Other",
};

export function DayDetailDialog({
  // State
  dayDetail,
  setDayDetail,
  // Child-scope filters
  selectedChildId,
  childFilter,
  setChildFilter,
  setParentFilter,
  // Reference data
  childList,
  members,
  custody,
  // Derived data + helpers
  childCustodyForDate,
  handoversByChild,
  memberName,
  parentIdx,
  typeClass,
  childSwatches,
  custodyBgColor,
  // Actions
  approve,
  reject,
  openEdit,
  setProposeFor,
  setProposeChanges,
  openJournalNote,
  exportEventToCalendar,
  openLinkedJournal,
  loadCustody,
  // Context
  isMediator,
  user,
}) {
  return (
    <Dialog open={!!dayDetail} onOpenChange={(v) => !v && setDayDetail(null)}>
      <DialogContent
        preventOutsideClose
        className="bg-white border-[#D9E1E3] max-h-[85vh] overflow-y-auto"
        data-testid="day-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            {dayDetail?.date &&
              dayDetail.date.toLocaleDateString(undefined, {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
          </DialogTitle>
          {/* v77.22.7 — Active scope breadcrumb. When the user opens
              the day-detail from a single-child filter, surface the
              scope clearly so the listing can never be mis-read as
              "all children's day". One-tap clear restores the
              master view AND closes the modal so the next tap on a
              day cell is clean. */}
          {(() => {
            const id =
              selectedChildId ||
              (childFilter.size === 1 ? Array.from(childFilter)[0] : null);
            if (!id) return null;
            const c = childList.find((x) => x.id === id);
            if (!c) return null;
            return (
              <div
                className="mt-1.5 inline-flex items-center gap-2 text-[11px] text-[#116B6E] bg-[#E8F0F1] border border-[#A6D7D9] rounded-sm px-2 py-1 self-start"
                data-testid="day-detail-scope-breadcrumb"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: c.color || "#1F9FA4" }}
                  aria-hidden="true"
                />
                <span>
                  Filtered to <span className="font-medium">{c.name}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setChildFilter(new Set());
                    setParentFilter(new Set());
                    setDayDetail(null);
                  }}
                  className="text-[#116B6E] underline-offset-2 hover:underline ml-1"
                  data-testid="day-detail-scope-clear"
                >
                  Clear filter
                </button>
              </div>
            );
          })()}
        </DialogHeader>

        {/* v77.22 — Per-child custody panel. */}
        {(() => {
          if (!dayDetail?.date) return null;
          const d = dayDetail.date;
          const isoKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const rows = childCustodyForDate(isoKey).filter((r) => {
            // v77.22.7 — Honour BOTH the global ChildScope picker
            // and the calendar's local chip filter.
            if (selectedChildId) return r.child_id === selectedChildId;
            if (childFilter.size > 0) return childFilter.has(r.child_id);
            return true;
          });
          if (rows.length === 0) return null;
          const dayHandovers = handoversByChild[isoKey] || [];
          const handoverByChild = Object.fromEntries(
            dayHandovers.map((h) => [h.child_id, h]),
          );
          return (
            <div className="space-y-2 mb-3" data-testid="day-detail-children">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                Children today
              </div>
              {rows.map((r) => {
                const ho = handoverByChild[r.child_id];
                return (
                  <div
                    key={r.child_id}
                    className="rounded-md border border-[#D9E1E3] bg-white p-3"
                    data-testid={`day-detail-child-${r.child_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: r.child_color || "#1F9FA4" }}
                          aria-hidden="true"
                        />
                        <span className="font-medium text-[#2E3A3F] truncate">
                          {r.child_name}
                        </span>
                      </div>
                      {!r.has_schedule ? (
                        <span
                          className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border border-[#D9E1E3] bg-[#F5F7F8] text-[#6F828A] rounded-sm"
                          data-testid={`day-detail-child-no-schedule-${r.child_id}`}
                        >
                          No schedule set
                        </span>
                      ) : r.parent_name ? (
                        <span
                          className="text-xs text-[#2E3A3F]"
                          data-testid={`day-detail-child-parent-${r.child_id}`}
                        >
                          With <span className="font-medium">{r.parent_name}</span>
                          {r.source === "override" && (
                            <span className="ml-1.5 text-[10px] uppercase tracking-[0.08em] text-[#8F6B1F]">
                              overridden
                            </span>
                          )}
                        </span>
                      ) : null}
                    </div>
                    {ho && (
                      <div
                        className="mt-2 text-xs text-[#0F5658] inline-flex items-center gap-1.5 bg-[#E0F2F2] border border-[#A7D8DA] rounded-sm px-2 py-1"
                        data-testid={`day-detail-child-handover-${r.child_id}`}
                      >
                        <ArrowLeftRight
                          className="w-3 h-3 flex-shrink-0"
                          strokeWidth={2}
                        />
                        <span>
                          Handover:{" "}
                          <span className="font-medium">
                            {memberName(ho.from_parent_id)}
                          </span>{" "}
                          to{" "}
                          <span className="font-medium">
                            {memberName(ho.to_parent_id)}
                          </span>
                          {ho.handover_time && (
                            <span className="ml-1 font-mono-data">
                              at {ho.handover_time}
                            </span>
                          )}
                          {ho.reason && (
                            <span className="ml-1 text-[#0F5658]/80">
                              · {ho.reason}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {/* v77.22.9 — Inline per-child swap action. */}
                    {!isMediator &&
                      r.has_schedule &&
                      r.parent_id &&
                      (() => {
                        const child = childList.find((cc) => cc.id === r.child_id);
                        const linkedIds = new Set(
                          (child?.sharing?.coparents || []).map((cp) => cp.user_id),
                        );
                        if (user?.id) linkedIds.add(user.id);
                        const otherIds = Array.from(linkedIds).filter(
                          (pid) => pid !== r.parent_id,
                        );
                        if (otherIds.length === 0) return null;
                        return (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {otherIds.map((pid) => {
                              const m = members.find((mm) => mm.id === pid);
                              const label =
                                m?.id === user?.id ? `you` : m?.name || "co-parent";
                              return (
                                <Button
                                  key={pid}
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm h-7 text-[11px] px-2"
                                  onClick={async () => {
                                    const reason = window.prompt(
                                      `Swap ${r.child_name}'s custody to ${m?.name || "this co-parent"} for this day?\n\nOptional reason (e.g. "doctor appointment"):`,
                                      "",
                                    );
                                    if (reason === null) return;
                                    try {
                                      await api.post("/custody/overrides", {
                                        child_id: r.child_id,
                                        date: isoKey,
                                        parent_id: pid,
                                        reason: reason || null,
                                      });
                                      toast.success(
                                        `Custody swapped to ${m?.name || "co-parent"}`,
                                      );
                                      loadCustody();
                                    } catch (err) {
                                      toast.error(
                                        err.response?.data?.detail || "Couldn't swap",
                                      );
                                    }
                                  }}
                                  data-testid={`day-detail-child-swap-${r.child_id}-${pid}`}
                                >
                                  Swap to {label}
                                </Button>
                              );
                            })}
                            {r.source === "override" && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm h-7 text-[11px] px-2"
                                onClick={async () => {
                                  if (
                                    !window.confirm(
                                      `Remove this override and restore ${r.child_name}'s schedule?`,
                                    )
                                  )
                                    return;
                                  try {
                                    await api.delete(
                                      `/custody/overrides/${isoKey}?child_id=${encodeURIComponent(r.child_id)}`,
                                    );
                                    toast.success("Override removed");
                                    loadCustody();
                                  } catch (err) {
                                    toast.error(
                                      err.response?.data?.detail || "Couldn't remove",
                                    );
                                  }
                                }}
                                data-testid={`day-detail-child-remove-override-${r.child_id}`}
                              >
                                Remove override
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Legacy household-wide custody block (mediator-only fallback). */}
        {(() => {
          if (!dayDetail?.date) return null;
          if (Object.keys(custody.byChild || {}).length > 0) return null;
          const d = dayDetail.date;
          const isoKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const a = custody.assignments[isoKey];
          const sched = custody.schedule;
          if (!a || !sched) return null;
          const parentName =
            members.find((m) => m.id === a.parent_id)?.name || "Unknown";
          return (
            <div
              className="rounded-md border p-3 mb-3"
              style={{
                backgroundColor: custodyBgColor(a.parent_id, sched, members),
                borderColor: "#D9E1E3",
              }}
              data-testid="day-detail-custody"
            >
              <div className="text-sm">
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                  Custody
                </span>
                <div className="font-medium text-[#2E3A3F]">{parentName}</div>
                {a.source === "override" && (
                  <div className="text-[11px] text-[#8F6B1F] mt-0.5">
                    Overridden{a.reason ? ` — ${a.reason}` : ""}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <ul className="space-y-3">
          {(dayDetail?.events || []).map((ev) => (
            <li
              key={ev.id}
              className="border border-[#D9E1E3] rounded-md p-3"
              data-testid={`day-event-${ev.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${typeClass(ev.type, parentIdx(ev.parent_id))}`}
                >
                  {TYPE_LABEL[ev.type] || ev.type}
                </span>
                {ev.recurrence && (
                  <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border border-[#D9E1E3] text-[#6F828A] bg-[#EEF3F4] rounded-sm inline-flex items-center gap-1">
                    <Repeat className="w-3 h-3" /> {ev.recurrence.freq}
                  </span>
                )}
                <span
                  className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${ev.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`}
                >
                  {ev.is_shared ? (
                    <Eye className="w-3 h-3" />
                  ) : (
                    <Lock className="w-3 h-3" />
                  )}
                  {ev.is_shared ? "Shared" : "Private"}
                </span>
                {ev.status === "proposed" && (
                  <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border border-[#825A41] text-[#4A3A2E] bg-[#F0E8E3] rounded-sm">
                    Change requested
                  </span>
                )}
                {ev.parent_id && (
                  <span className="text-xs text-[#6F828A] font-mono-data">
                    {members.find((m) => m.id === ev.parent_id)?.name}
                  </span>
                )}
                {ev.linked_journal_count > 0 && (
                  <button
                    type="button"
                    onClick={() => openLinkedJournal(ev)}
                    title={`${ev.linked_journal_count} linked journal ${ev.linked_journal_count === 1 ? "entry" : "entries"}`}
                    className="text-[11px] uppercase tracking-[0.08em] font-semibold px-2.5 py-1 bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-sm inline-flex items-center gap-1.5 transition-colors shadow-sm"
                    data-testid={`day-event-linked-journal-${ev.id}`}
                  >
                    <BookOpen className="w-3 h-3" strokeWidth={2.2} />
                    {ev.linked_journal_count}{" "}
                    {ev.linked_journal_count === 1
                      ? "Journal note"
                      : "Journal notes"}
                  </button>
                )}
              </div>
              <div className="font-medium text-[#2E3A3F] mt-2">{ev.title}</div>
              <div className="text-sm text-[#6F828A] font-mono-data mt-0.5">
                {formatDT(ev.start)} → {formatDT(ev.end)}
              </div>
              {childSwatches(ev.child_ids).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {childSwatches(ev.child_ids).map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 text-[11px] text-[#6F828A]"
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: c.color || "#1F9FA4" }}
                      ></span>
                      {c.name}
                    </span>
                  ))}
                </div>
              )}
              {ev.notes && (
                <div className="text-sm text-[#6F828A] mt-2 whitespace-pre-wrap">
                  {ev.notes}
                </div>
              )}
              {ev.status === "proposed" && ev.proposed_changes && (
                <div className="mt-2 p-2 bg-[#F0E8E3] border border-[#D9E1E3] rounded-sm text-xs">
                  <div className="uppercase tracking-[0.1em] text-[#6F828A] mb-1">
                    Requested changes
                  </div>
                  {ev.proposed_changes.start && (
                    <div className="font-mono-data">
                      Start → {formatDT(ev.proposed_changes.start)}
                    </div>
                  )}
                  {ev.proposed_changes.end && (
                    <div className="font-mono-data">
                      End → {formatDT(ev.proposed_changes.end)}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {!isMediator &&
                  ev.status === "proposed" &&
                  ev.proposed_by !== user.id && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => approve(ev.id)}
                        className="bg-[#1F9FA4] hover:bg-[#116B6E] rounded-sm"
                        data-testid={`day-approve-${ev.id}`}
                      >
                        Agree
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reject(ev.id)}
                        className="border-[#D9E1E3] rounded-sm"
                        data-testid={`day-reject-${ev.id}`}
                      >
                        Not agree
                      </Button>
                    </>
                  )}
                {!isMediator && ev.status === "confirmed" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(ev)}
                      className="border-[#D9E1E3] rounded-sm"
                      data-testid={`day-edit-${ev.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setProposeFor(ev);
                        setProposeChanges({
                          start: toLocalInput(ev.start),
                          end: toLocalInput(ev.end),
                          note: "",
                        });
                      }}
                      className="border-[#D9E1E3] rounded-sm"
                      data-testid={`day-propose-${ev.id}`}
                    >
                      Request change
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openJournalNote(ev)}
                      className="border-[#D9E1E3] rounded-sm"
                      data-testid={`day-journal-note-${ev.id}`}
                    >
                      <BookOpen className="w-3 h-3 mr-1" /> Make journal note
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => exportEventToCalendar(ev)}
                      className="border-[#D9E1E3] rounded-sm"
                      data-testid={`day-add-to-calendar-${ev.id}`}
                      title="Add to Apple, Google or Outlook calendar"
                    >
                      <CalendarPlus className="w-3 h-3 mr-1" /> Add to calendar
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export default DayDetailDialog;
